import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Calendar, MapPin, Sparkles } from 'lucide-react';

const HeroSection = () => {
  const couplePhotos = [
    'https://customer-assets.emergentagent.com/job_63c4351d-8af9-4b2d-88d7-140fc0bb4997/artifacts/nhtv50xo_b5308792-25ec-4d1e-8c9b-21a01bf0da8b.jpg',
    'https://customer-assets.emergentagent.com/job_63c4351d-8af9-4b2d-88d7-140fc0bb4997/artifacts/8ggr4h60_d2604fac-353e-473a-92aa-097f5c27fc8a.jpg',
    'https://customer-assets.emergentagent.com/job_63c4351d-8af9-4b2d-88d7-140fc0bb4997/artifacts/4pyj9dwp_7f5219cd-2715-4c4e-b824-baa63e1dedfa.jpg'
  ];

  const scrollToSection = (id) => {
    const element = document.querySelector(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center py-20 overflow-hidden">
      {/* Background image with overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ 
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_63c4351d-8af9-4b2d-88d7-140fc0bb4997/artifacts/8ggr4h60_d2604fac-353e-473a-92aa-097f5c27fc8a.jpg')`
        }}
      />
      {/* Green overlay with gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/95 via-primary/90 to-primary/85" />
      {/* Elegant overlay pattern */}
      <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
      
      <div className="section-container relative z-10 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Names & Info */}
          <motion.div 
            className="text-center lg:text-left space-y-8 text-white"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <Sparkles className="w-4 h-4 text-[#E6C76B]" />
              <span className="text-sm text-[#E6C76B] font-medium">Our Wedding Weekend</span>
            </div>
            
            <div className="space-y-6">
              <h1 className="font-serif text-6xl sm:text-7xl lg:text-8xl font-normal tracking-tight leading-[0.95] text-white">
                Robyn<br/>
                <span 
                  className="text-7xl sm:text-8xl lg:text-9xl"
                  style={{ 
                    backgroundImage: 'linear-gradient(135deg, #F9E27D 0%, #D4AF37 40%, #B8962E 60%, #F6E27A 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    display: 'inline-block'
                  }}
                >
                  &
                </span> Angelo
              </h1>
              
              <div className="flex flex-col gap-3 text-white/90">
                <div className="flex items-center gap-3 justify-center lg:justify-start">
                  <Calendar className="w-5 h-5 text-[#E6C76B]" />
                  <span className="text-lg">September 18-21, 2026</span>
                </div>
                <div className="flex flex-col gap-2 justify-center lg:justify-start">
                  <div className="flex items-center gap-3 justify-center lg:justify-start">
                    <MapPin className="w-5 h-5 text-[#E6C76B]" />
                    <span className="text-lg font-semibold">Sherwood Farm</span>
                  </div>
                  <div className="flex flex-col text-sm text-white/70 pl-8 justify-center lg:justify-start text-center lg:text-left">
                    <span>123-125 Featherstone Road</span>
                    <span>Sherwood, NSW 2450</span>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-xl text-white/80 max-w-lg mx-auto lg:mx-0 leading-relaxed">
              Join us for a magical weekend celebration. 
              Ceremony <span 
                className="font-semibold"
                style={{ 
                  backgroundImage: 'linear-gradient(135deg, #F9E27D 0%, #D4AF37 40%, #B8962E 60%, #F6E27A 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >September 20th</span>.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4">
              <Button 
                size="lg" 
                className="text-base h-14 px-10 text-primary rounded-full text-lg font-bold shadow-2xl hover:scale-105 transition-all duration-300"
                style={{ 
                  backgroundImage: 'linear-gradient(135deg, #F9E27D 0%, #D4AF37 40%, #B8962E 60%, #F6E27A 100%)',
                  boxShadow: '0 4px 20px rgba(212, 175, 55, 0.4)'
                }}
                onClick={() => scrollToSection('#upload')}
                data-testid="hero-upload-photos-button"
              >
                Share Your Photos
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="text-base h-14 px-10 rounded-full border-2 border-white/30 hover:border-white/50 text-white backdrop-blur-sm transition-all duration-300"
                onClick={() => scrollToSection('#gallery')}
                data-testid="hero-view-gallery-button"
              >
                View Gallery
              </Button>
            </div>
          </motion.div>

          {/* Right: Photo Grid */}
          <motion.div 
            className="order-1 lg:order-2"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            <div className="grid grid-cols-2 gap-6">
              <div className="col-span-2">
                <motion.div
                  whileHover={{ scale: 1.02, rotate: -0.5 }}
                  transition={{ duration: 0.3 }}
                  className="relative aspect-[16/9] rounded-3xl overflow-hidden shadow-2xl border-4 border-white/20"
                >
                  <img
                    src={couplePhotos[0]}
                    alt="Robyn and Angelo"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                </motion.div>
              </div>
              
              <div className="col-span-1">
                <motion.div
                  whileHover={{ scale: 1.05, rotate: 1 }}
                  transition={{ duration: 0.3 }}
                  className="relative aspect-square rounded-3xl overflow-hidden shadow-xl border-4 border-white/20"
                >
                  <img
                    src={couplePhotos[1]}
                    alt="Celebrating"
                    className="w-full h-full object-cover"
                  />
                </motion.div>
              </div>
              
              <div className="col-span-1">
                <motion.div
                  whileHover={{ scale: 1.05, rotate: -1 }}
                  transition={{ duration: 0.3 }}
                  className="relative aspect-square rounded-3xl overflow-hidden shadow-xl border-4 border-white/20"
                >
                  <img
                    src={couplePhotos[2]}
                    alt="Embracing"
                    className="w-full h-full object-cover"
                  />
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
